import { UseScrollTriggerOptions } from '@mui/material/useScrollTrigger/useScrollTrigger';
import { Store } from 'reducers/rootReducer';

type TrackerData = {
  currentQuestion: number;
  questionsOnPage: number;
  totalQuestions: number;
  message: string;
  onlyShowProgress: boolean;
} | null;

export type State = {
  projectId: number | null;
  assessmentId: number | null;
  applicationType: string | null;
  jobApplicationId: number | null;
  trackerData: TrackerData | null;
  instructionsUnderstood: boolean;
  clientLogo: string | null;
  signUpProjectId: number | null;
  isStagesOnlyApplicationFlow: boolean;
};

const initialState: State = {
  projectId: null,
  assessmentId: null,
  applicationType: null,
  jobApplicationId: null,
  trackerData: null,
  instructionsUnderstood: false,
  clientLogo: null,
  signUpProjectId: null,
  isStagesOnlyApplicationFlow: false,
};

// Actions
const SET_PROJECT_ID = 'app/virtualAssessments/SET_PROJECT_ID';
const SET_APPLICATION_TYPE = 'app/virtualAssessments/SET_APPLICATION_TYPE';
const SET_ASSESSMENT_ID = 'app/virtualAssessments/SET_ASSESSMENT_ID';
const SET_JOB_APPLICATION_ID = 'app/virtualAssessments/SET_JOB_APPLICATION_ID';
const SET_TRACKER_DATA = 'app/virtualAssessments/SET_TRACKER_DATA';
const SET_INSTRUCTIONS_UNDERSTOOD =
  'app/virtualAssessments/SET_INSTRUCTIONS_UNDERSTOOD';
const SET_CLIENT_LOGO = 'app/virtualAssessments/SET_CLIENT_LOGO';
const SET_SIGN_UP_PROJECT_ID = 'app/virtualAssessments/SET_SIGN_UP_PROJECT_ID';
const SET_IS_STAGES_ONLY_APPLICATION_FORM =
  'app/virtualAssessments/SET_IS_STAGES_ONLY_APPLICATION_FORM';

// Reducer
type Action =
  | {
      type: typeof SET_PROJECT_ID;
      payload: number;
    }
  | {
      type: typeof SET_APPLICATION_TYPE;
      payload: string;
    }
  | {
      type: typeof SET_ASSESSMENT_ID;
      payload: number;
    }
  | {
      type: typeof SET_JOB_APPLICATION_ID;
      payload: number;
    }
  | {
      type: typeof SET_TRACKER_DATA;
      payload: TrackerData;
    }
  | {
      type: typeof SET_INSTRUCTIONS_UNDERSTOOD;
      payload: boolean;
    }
  | {
      type: typeof SET_CLIENT_LOGO;
      payload: string;
    }
  | {
      type: typeof SET_SIGN_UP_PROJECT_ID;
      payload: number | null;
    }
  | {
      type: typeof SET_IS_STAGES_ONLY_APPLICATION_FORM;
      payload: boolean;
    };

export default function reducer(state = initialState, action: Action): State {
  switch (action.type) {
    case SET_PROJECT_ID:
      return {
        ...state,
        projectId: action.payload,
      };
    case SET_APPLICATION_TYPE:
      return {
        ...state,
        applicationType: action.payload,
      };
    case SET_ASSESSMENT_ID:
      return {
        ...state,
        assessmentId: action.payload,
      };
    case SET_JOB_APPLICATION_ID:
      return {
        ...state,
        jobApplicationId: action.payload,
      };
    case SET_TRACKER_DATA:
      return {
        ...state,
        trackerData: action.payload,
      };
    case SET_INSTRUCTIONS_UNDERSTOOD:
      return {
        ...state,
        instructionsUnderstood: action.payload,
      };
    case SET_CLIENT_LOGO:
      return {
        ...state,
        clientLogo: action.payload,
      };
    case SET_SIGN_UP_PROJECT_ID:
      return {
        ...state,
        signUpProjectId: action.payload,
      };
    case SET_IS_STAGES_ONLY_APPLICATION_FORM:
      return {
        ...state,
        isStagesOnlyApplicationFlow: action.payload,
      };
    default:
      return state;
  }
}

// Action creators
export const setProjectId = (payload: number | null) => ({
  type: SET_PROJECT_ID,
  payload,
});
export const setApplicationType = (payload: string | null) => ({
  type: SET_APPLICATION_TYPE,
  payload,
});
export const setAssessmentId = (payload: number | null) => ({
  type: SET_ASSESSMENT_ID,
  payload,
});
export const setjobApplicationId = (payload: number) => ({
  type: SET_JOB_APPLICATION_ID,
  payload,
});
export const setTrackerData = (payload: TrackerData) => ({
  type: SET_TRACKER_DATA,
  payload,
});
export const setInstructionsUnderstood = (payload: boolean) => ({
  type: SET_INSTRUCTIONS_UNDERSTOOD,
  payload,
});
export const setClientLogo = (payload: number) => ({
  type: SET_CLIENT_LOGO,
  payload,
});
export const setSignUpProjectId = (payload: number | null) => ({
  type: SET_SIGN_UP_PROJECT_ID,
  payload,
});

export const setIsStagesOnlyApplicationForm = (payload: boolean) => ({
  type: SET_IS_STAGES_ONLY_APPLICATION_FORM,
  payload,
});

// Selectors
export const selectProjectId = (app: Store) => app.virtualAssessments.projectId;
export const selectAssessmentId = (app: Store) =>
  app.virtualAssessments.assessmentId;
export const selectJobApplicationId = (app: Store) =>
  app.virtualAssessments.jobApplicationId;
export const selectApplicationType = (app: Store) =>
  app.virtualAssessments.applicationType;
export const selectTrackerData = (app: Store) =>
  app.virtualAssessments.trackerData;
export const selectInstructionsUnderstood = (app: Store) =>
  app.virtualAssessments.instructionsUnderstood;
export const selectClientLogo = (app: Store) =>
  app.virtualAssessments.clientLogo;
export const selectSignUpProjectId = (app: Store) =>
  app.virtualAssessments.signUpProjectId;
export const selectIsStagesOnlyApplicationForm = (app: Store) =>
  app.virtualAssessments.isStagesOnlyApplicationFlow;
